import { createUniqueId } from "solid-js";
import { Button, ButtonLink } from "~/common/button";
import { CheckboxField, Form, InputField } from "~/common/form";
import { TextLinkA } from "~/common/text_link";
import { useForm } from "~/inertia";
import { splitAround, usePatientText } from "~/patient_i18n/i18n";
import { optInPaths, type OptInOwner } from "./paths";

type OptInForm = {
  firstName: string;
  lastName: string;
  phone: string;
  consent: boolean;
};

/** The sign-up form itself, shared by the live opt-in page and the details page preview. */
export function SmsOptInForm(props: {
  owner: OptInOwner;
  locationName: string;
  consentLabel: string;
}) {
  const { t } = usePatientText();
  const formId = createUniqueId();
  const form = useForm<OptInForm>({
    firstName: "",
    lastName: "",
    phone: "",
    consent: false,
  });

  return (
    <>
      <div class="mb-6">
        <h1 class="text-2xl font-semibold">{t("sms_opt_in.form_title")}</h1>
        <p class="mt-2 text-sm text-typography-secondary">
          {t("sms_opt_in.form_intro", { name: props.locationName })}
        </p>
      </div>

      <Form
        id={formId}
        onSubmit={(e) => {
          e.preventDefault();
          form.post(optInPaths(props.owner).optIn);
        }}
      >
        <div class="flex flex-col gap-4 sm:flex-row">
          <InputField
            label={t("sms_opt_in.first_name")}
            required
            value={form.data.firstName}
            errors={form.errors.firstName}
            onChange={(e) => form.setData("firstName", e.target.value)}
          />
          <InputField
            label={t("sms_opt_in.last_name")}
            required
            value={form.data.lastName}
            errors={form.errors.lastName}
            onChange={(e) => form.setData("lastName", e.target.value)}
          />
        </div>

        <InputField
          label={t("sms_opt_in.mobile_phone")}
          type="tel"
          required
          value={form.data.phone}
          errors={form.errors.phone}
          onChange={(e) => form.setData("phone", e.target.value)}
        />

        <ProgramDisclosures locationName={props.locationName} />

        <CheckboxField
          label={props.consentLabel}
          checked={form.data.consent}
          errors={form.errors.consent}
          onChange={(e) => form.setData("consent", e.currentTarget.checked)}
        />

        <p class="text-sm font-medium text-typography-primary">
          {t("sms_opt_in.consent_not_condition")}
        </p>

        <LegalLinks owner={props.owner} />

        <div class="mt-2 flex justify-end gap-3">
          <ButtonLink href={optInPaths(props.owner).optOut} variant="secondary">
            {t("sms_opt_in.no_thanks")}
          </ButtonLink>
          <Button form={formId} type="submit" variant="primary" loading={form.processing}>
            {t("sms_opt_in.sign_up")}
          </Button>
        </div>
      </Form>
    </>
  );
}

function ProgramDisclosures(props: { locationName: string }) {
  const { t } = usePatientText();

  return (
    <div class="rounded-xl border border-brand-subtle bg-brand-light px-4 py-4 text-sm text-typography-secondary">
      <p class="font-medium text-typography-primary">{t("sms_opt_in.about_program")}</p>
      <dl class="mt-3 space-y-3">
        <div>
          <dt class="font-medium text-typography-primary">{t("sms_opt_in.message_types_label")}</dt>
          <dd>{t("sms_opt_in.message_types")}</dd>
        </div>
        <div>
          <dt class="font-medium text-typography-primary">{t("sms_opt_in.frequency_label")}</dt>
          <dd>{t("sms_opt_in.frequency", { name: props.locationName })}</dd>
        </div>
      </dl>
      <ul class="mt-3 list-disc space-y-1 pl-5">
        <li>{t("sms_opt_in.rates")}</li>
        <li>{t("sms_opt_in.stop_help")}</li>
        <li>{t("sms_opt_in.not_condition")}</li>
      </ul>
    </div>
  );
}

// "Review our %{terms} and %{privacy}.": the words around the two links, in the page's language.
function LegalLinks(props: { owner: OptInOwner }) {
  const { t } = usePatientText();
  const [beforeTerms, rest] = splitAround(t("sms_opt_in.legal_links"), "terms");
  const [between, afterPrivacy] = splitAround(rest, "privacy");

  return (
    <p class="text-sm text-typography-secondary">
      {beforeTerms}
      <TextLinkA
        href={optInPaths(props.owner).termsAndConditions}
        destination="new-tab"
        class="text-brand-primary"
      >
        {t("legal_pages.terms_title")}
      </TextLinkA>
      {between}
      <TextLinkA
        href={optInPaths(props.owner).privacyPolicy}
        destination="new-tab"
        class="text-brand-primary"
      >
        {t("legal_pages.privacy_title")}
      </TextLinkA>
      {afterPrivacy}
    </p>
  );
}
